import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Mic, Headphones, Play, ExternalLink, Sparkles, Lightbulb, Clock, Users } from 'lucide-react';
import { TiltCard } from '../components/home/TiltCard';
import { ScrollDepthReveal } from '../components/home/ScrollDepthReveal';

const WICKWISE_LOGO = "https://customer-assets.emergentagent.com/job_d19a6233-9851-427c-8ac6-e72b3dd8c600/artifacts/me5zbrhi_3.png";

const InsightsPage = () => {
  const podcastInfo = {
    name: "Wickwise — hosted by Bivas Mishra",
    description: "A long-form communication podcast. One guest. 35–50 minutes. A fixed arc that moves from origin to craft to the single lit idea — what we call The Lamp Question. You leave with one usable idea, every time.",
    spotifyUrl: "https://open.spotify.com/show/6l2wsDkyvwkHo96GG3DwUq",
  };

  const episodes = [
    { title: "Leadership in the Modern Era", description: "How leadership has evolved in the digital age and what it takes to lead effectively today.", duration: "45 min", spotifyUrl: podcastInfo.spotifyUrl, category: "Leadership" },
    { title: "Entrepreneurship Journey", description: "Lessons from entrepreneurs who built from scratch — their failures and victories.", duration: "38 min", spotifyUrl: podcastInfo.spotifyUrl, category: "Entrepreneurship" },
    { title: "Building a Personal Brand", description: "Becoming a thought leader through authentic storytelling and strategic positioning.", duration: "42 min", spotifyUrl: podcastInfo.spotifyUrl, category: "Personal Growth" },
    { title: "The Future of Work", description: "Remote work, AI integration, and adapting to a rapidly changing workplace.", duration: "50 min", spotifyUrl: podcastInfo.spotifyUrl, category: "Career" },
    { title: "Cross-Cultural Business", description: "Navigating international business relationships and cultural nuances in global markets.", duration: "35 min", spotifyUrl: podcastInfo.spotifyUrl, category: "Business" },
    { title: "Student to Professional", description: "Bridging academia and industry — skills every student needs before the corporate world.", duration: "40 min", spotifyUrl: podcastInfo.spotifyUrl, category: "Education" },
  ];

  const platforms = [
    { name: "Spotify", icon: "🎵", url: podcastInfo.spotifyUrl },
    { name: "Pocket Casts", icon: "🎧", url: "https://pocketcasts.com/podcast/unmic-show-with-bivas-mishra/7800d9d0-c0c7-0139-f634-0acc26574db2" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-16 lg:py-24 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/[0.05] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/[0.04] rounded-full blur-3xl" />

          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Logo card */}
              <ScrollDepthReveal direction="left" className="text-center lg:text-left">
                <TiltCard maxTilt={8} testId="insights-logo-tilt" className="inline-block">
                  <div className="absolute -inset-3 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-3xl blur-lg opacity-60" />
                  <div className="relative bg-[#fdf5e6] rounded-3xl p-8 shadow-2xl">
                    <img
                      src={WICKWISE_LOGO}
                      alt="Wickwise"
                      data-testid="wickwise-logo"
                      className="w-full max-w-sm h-auto mx-auto"
                    />
                  </div>
                </TiltCard>
              </ScrollDepthReveal>

              {/* Podcast Info */}
              <ScrollDepthReveal direction="right" delay={0.1}>
                <span className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-6">
                  <Sparkles size={14} className="mr-2" />
                  Long-form Communication Podcast
                </span>

                <h1
                  className="text-4xl lg:text-5xl font-bold text-white mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Wickwise Pod
                </h1>

                <p className="text-xl text-neutral-300 font-medium mb-4">
                  {podcastInfo.name}
                </p>

                <p className="text-neutral-400 text-lg leading-relaxed mb-8">
                  {podcastInfo.description}
                </p>

                {/* Listen Now Buttons */}
                <div className="flex flex-wrap gap-4">
                  {platforms.map((platform, index) => (
                    <a
                      key={index}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-white text-black font-semibold rounded-full hover:bg-neutral-200 hover:shadow-lg transition-all"
                    >
                      <span className="mr-2 text-xl">{platform.icon}</span>
                      Listen on {platform.name}
                      <ExternalLink size={16} className="ml-2" />
                    </a>
                  ))}
                </div>
              </ScrollDepthReveal>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 lg:py-20 bg-black border-y border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollDepthReveal className="text-center mb-10">
              <h2
                className="text-3xl lg:text-4xl font-bold text-white mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                The Wickwise Promise
              </h2>
              <p className="text-neutral-300 font-medium">Global in reach. Indian in soul.</p>
            </ScrollDepthReveal>
            <div className="max-w-none space-y-5">
              <p className="text-neutral-200 leading-relaxed text-base lg:text-lg">
                Wickwise is a long-form communication podcast hosted by <strong className="text-white font-semibold">Bivas Mishra</strong> — Senior Business Analyst, community builder, and career thinker based in Bengaluru.
              </p>
              <p className="text-neutral-200 leading-relaxed text-base lg:text-lg">
                What started as <em className="text-neutral-100 italic">The UnMic Show</em> — a self-improvement and inspiration podcast connecting listeners with accomplished minds across health, career, lifestyle, and meaning — has evolved into something sharper. Wickwise keeps the warmth and the curiosity. It drops the vague. Every episode is built around one promise: <strong className="text-white font-semibold">you leave with one usable idea.</strong>
              </p>
              <p className="text-neutral-200 leading-relaxed text-base lg:text-lg">
                The format is deliberate. <strong className="text-white font-semibold">One guest. Thirty-five to fifty minutes.</strong> A fixed arc that moves from origin to craft to the single lit idea — what we call <strong className="text-white font-semibold">The Lamp Question</strong>. We talk to entrepreneurs, specialists, authors, and practitioners. We go global in reach and stay Indian in soul.
              </p>
              <p className="text-xl text-white font-semibold pt-4 border-t border-white/10">
                Wickwise is not background noise. It is a conversation worth sitting down for.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-[#0a0a0c]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-3 gap-8 text-center">
              <ScrollDepthReveal>
                <Users className="w-8 h-8 text-white mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">One Guest</div>
                <div className="text-neutral-400 text-sm">per episode</div>
              </ScrollDepthReveal>
              <ScrollDepthReveal delay={0.08}>
                <Clock className="w-8 h-8 text-white mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">35–50 min</div>
                <div className="text-neutral-400 text-sm">deliberate length</div>
              </ScrollDepthReveal>
              <ScrollDepthReveal delay={0.16}>
                <Lightbulb className="w-8 h-8 text-white mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">One Idea</div>
                <div className="text-neutral-400 text-sm">The Lamp Question</div>
              </ScrollDepthReveal>
            </div>
          </div>
        </section>

        {/* Episodes Grid */}
        <section className="py-16 lg:py-24 bg-black">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollDepthReveal className="text-center mb-12">
              <h2
                className="text-3xl lg:text-4xl font-bold text-white mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Featured Conversations
              </h2>
              <p className="text-neutral-400 max-w-2xl mx-auto">
                Conversations worth sitting down for — built around the single lit idea.
              </p>
            </ScrollDepthReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((episode, index) => (
                <ScrollDepthReveal key={index} delay={Math.min(index * 0.06, 0.3)}>
                  <TiltCard maxTilt={12} hoverScale={1.04} testId={`episode-tilt-${index}`}>
                    <a
                      href={episode.spotifyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block bg-white/[0.04] rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all shadow-lg"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-neutral-200">
                          {episode.category}
                        </span>
                        <span className="text-neutral-500 text-sm flex items-center">
                          <Headphones size={14} className="mr-1" />
                          {episode.duration}
                        </span>
                      </div>

                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-neutral-300 transition-colors">
                        {episode.title}
                      </h3>

                      <p className="text-neutral-400 text-sm mb-4 line-clamp-2">
                        {episode.description}
                      </p>

                      <div className="flex items-center text-white font-semibold text-sm group-hover:text-neutral-300 transition-colors">
                        <Play size={16} className="mr-2 fill-current" />
                        Play Episode
                        <ExternalLink size={14} className="ml-2" />
                      </div>
                    </a>
                  </TiltCard>
                </ScrollDepthReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Spotify Embed */}
        <section className="py-16 lg:py-24 bg-[#0a0a0c]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2
                className="text-3xl font-bold text-white mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Listen Now on Spotify
              </h2>
              <p className="text-neutral-400">Stream directly from your favorite podcast platform</p>
            </div>

            <div className="bg-white/[0.04] rounded-3xl p-6 shadow-xl border border-white/10">
              <iframe
                style={{ borderRadius: '12px' }}
                src="https://open.spotify.com/embed/show/6l2wsDkyvwkHo96GG3DwUq?utm_source=generator&theme=0"
                width="100%"
                height="352"
                frameBorder="0"
                allowFullScreen=""
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="Wickwise Podcast"
              />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-24 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-3xl blur-xl" />
              <div className="relative bg-white rounded-3xl p-12 shadow-2xl">
                <h2
                  className="text-3xl lg:text-4xl font-bold text-black mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Be a Guest on Wickwise
                </h2>
                <p className="text-neutral-700 text-lg mb-8 max-w-2xl mx-auto">
                  Have a craft, a story, a lit idea? Let's have a conversation worth sitting down for.
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center px-8 py-4 bg-black text-white font-semibold rounded-full hover:shadow-lg hover:scale-105 hover:bg-neutral-800 transition-all"
                >
                  <Mic size={18} className="mr-2" />
                  Get in Touch
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default InsightsPage;
